import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  listMessageReports,
  resolveMessageReport,
  type MessageReportItem,
  type MessageReportStatus,
} from '../services/adminModerationApi';
import { ContentPageLayout } from '../components/Shared';

const STATUS_OPTIONS: Array<MessageReportStatus | 'all'> = [
  'pending',
  'reviewed',
  'dismissed',
  'action_taken',
  'all',
];

const statusColor = (status: string) => {
  if (status === 'pending') return 'warning';
  if (status === 'action_taken') return 'error';
  if (status === 'dismissed') return 'default';
  return 'info';
};

const MessageModerationPage: React.FC = () => {
  const [status, setStatus] = useState<MessageReportStatus | 'all'>('pending');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<MessageReportItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMessageReports({ page, limit: 25, status });
      setReports(res.data?.reports || []);
      setTotalPages(res.data?.pagination?.pages || 0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResolve = async (
    reportId: string,
    action: MessageReportStatus,
    hideMessage = false,
  ) => {
    setActingId(reportId);
    try {
      await resolveMessageReport(reportId, action, hideMessage);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update report');
    } finally {
      setActingId(null);
    }
  };

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">
            Message moderation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review reported shared-discussion messages (documents and posts).
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as MessageReportStatus | 'all');
            }}
            sx={{ minWidth: 160 }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reported</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Context</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No reports in this queue.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report._id} hover>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap>
                        {report.messageId?.contentPlain || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(report.createdAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.reason}</Typography>
                      {report.details ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {report.details}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {report.hostContextType ? (
                        <Typography variant="caption">
                          {report.hostContextType} · {String(report.hostContextId || '').slice(-6)}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {report.reporterId?.nickname || report.reporterId?.email || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={report.status} color={statusColor(report.status) as 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      {report.status === 'pending' ? (
                        <Box display="flex" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
                          <Button
                            size="small"
                            disabled={actingId === report._id}
                            onClick={() => handleResolve(report._id, 'dismissed')}
                          >
                            Dismiss
                          </Button>
                          <Button
                            size="small"
                            disabled={actingId === report._id}
                            onClick={() => handleResolve(report._id, 'reviewed')}
                          >
                            Reviewed
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={actingId === report._id}
                            onClick={() => handleResolve(report._id, 'action_taken', true)}
                          >
                            Hide message
                          </Button>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 ? (
        <Box display="flex" justifyContent="center" gap={1} mt={2}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            Page {page} / {totalPages}
          </Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </Box>
      ) : null}
    </Box>
    </ContentPageLayout>
  );
};

export default MessageModerationPage;
